import React from 'react'
import { SelectDropdownItem } from '@sketch/components'
import {
  IndexLayout,
  IndexLayoutContent,
  indexLayoutSymbols,
} from '@sketch/modules-common'
import { HeaderLink } from '@sketch/modules-common/src/components/IndexLayoutOld/IndexLayoutHeader'
import { LayoutPortal } from '@sketch/utils'
import { useGetClientCurrency } from '../../operations/useClientCurrency'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

import { ReactComponent as USIcon } from '@sketch/icons/us-flag-24'
import { ReactComponent as EUIcon } from '@sketch/icons/eu-flag-24'
import {
  Abbr,
  Icon,
  Select,
  SelectInside,
} from './WorkspaceSubscribeLayout.styles'

const CURRENCY_TO_COMPONENT = {
  USD: { text: 'US Dollar', symbol: 'USD', icon: USIcon },
  EUR: { text: 'Euro', symbol: 'EUR', icon: EUIcon },
}

interface CurrencySelectorProps {
  selectedCurrency: Currency
  onCurrencyChange: (currency: Currency) => void
}

export const CurrencySelector = (props: CurrencySelectorProps) => {
  const { onCurrencyChange, selectedCurrency } = props
  const { data } = useGetClientCurrency()

  const availableCurrencies = data?.currencies.available || []

  if (availableCurrencies.length === 1) {
    return null
  }

  return (
    <Select
      placeholder=""
      items={availableCurrencies}
      headerItem
      value={selectedCurrency}
      renderItem={(value, { setItem, ...a11yOptions }) => {
        const { text } = CURRENCY_TO_COMPONENT[value]
        return (
          <SelectDropdownItem
            onClick={() => setItem()}
            text={text}
            {...a11yOptions}
          />
        )
      }}
      renderButtonText={value => {
        const { icon, text, symbol } = CURRENCY_TO_COMPONENT[value]

        return (
          <SelectInside>
            <Icon as={icon} />
            <Abbr title={text}>{symbol}</Abbr>
          </SelectInside>
        )
      }}
      onChange={onCurrencyChange}
    />
  )
}

interface WorkspaceSubscribeHeaderProps extends CurrencySelectorProps {
  hideCurrencySelector?: boolean
}

export const WorkspaceSubscribeHeader = (
  props: WorkspaceSubscribeHeaderProps
) => (
  <LayoutPortal token={indexLayoutSymbols.header}>
    <HeaderLink headerLink="workspace-shares" ignoreWorkspaceMatch>
      {!props.hideCurrencySelector && (
        <span>
          <CurrencySelector
            selectedCurrency={props.selectedCurrency}
            onCurrencyChange={props.onCurrencyChange}
          />
        </span>
      )}
    </HeaderLink>
  </LayoutPortal>
)

export const WorkspaceSubscribeLayout = (
  props: React.PropsWithChildren<WorkspaceSubscribeHeaderProps>
) => {
  const { children, ...otherProps } = props

  return (
    <IndexLayout
      fullWidth
      render={() => (
        <IndexLayoutContent>
          <WorkspaceSubscribeHeader {...otherProps} />
          {children}
        </IndexLayoutContent>
      )}
    ></IndexLayout>
  )
}
